import { useCallback, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { AppIcon } from '../ui/icon'
import { mdiAlertCircleOutline, mdiCheckCircleOutline, mdiUpload } from '@mdi/js'
import { Button } from '../ui/button'
import { Progress } from '../ui/progress'
import { UploadError } from '../../api/upload-with-progress'
import { ChunkedUploadError, type DocumentUploadProgress } from '../../api/chunked-upload'
import { useUploadDocument } from '../../hooks/useQueries'
import { collectDroppedFiles, filesFromFileList, appendOriginalPath, type DroppedFile } from '../../lib/dropped-files'
import { ImportSelect } from './ImportSelect'

interface ImportDepositZoneProps {
    org: string
    importUid: string
}

type DepositStatus = 'uploading' | 'success' | 'failed'

interface DepositFile {
    id: string
    name: string
    status: DepositStatus
    /** Upload percentage, 0 to 100. */
    progress: number
    /** Set once a chunked upload's bytes are all sent and the server assembles them. */
    assembling?: boolean
    /** Why it failed, already translated. */
    reason?: string
    source: DroppedFile
}

/**
 * Deposit zone on the import detail page (spec 2026-08-16-05 §3).
 *
 * The natural "I declared an import, now I feed it" path: the import is
 * pre-selected and **locked** (the selector is shown read-only so the user can
 * see what the drop feeds, but cannot retarget it from here), and every upload
 * carries the `X-Import-Uid` header of this very import.
 *
 * Folder drops keep their `original_path` exactly like the other upload
 * surfaces — the same `dropped-files` helpers do the walking.
 */
export function ImportDepositZone({ org, importUid }: ImportDepositZoneProps) {
    const { t } = useTranslation('imports')
    const queryClient = useQueryClient()
    const fileInputRef = useRef<HTMLInputElement>(null)
    const [dragOver, setDragOver] = useState(false)
    const [files, setFiles] = useState<DepositFile[]>([])
    const uploadMutation = useUploadDocument(org, importUid)

    const refreshImportViews = useCallback(() => {
        queryClient.invalidateQueries({
            predicate: query => {
                const key = query.queryKey as unknown[]
                const path = key[1]
                return (
                    key[0] === 'get' &&
                    (path === '/api/v1/organizations/{orgCode}/imports/{importUid}/summary' ||
                        path === '/api/v1/organizations/{orgCode}/documents')
                )
            },
        })
    }, [queryClient])

    const failureReason = useCallback(
        (err: unknown): string => {
            if (err instanceof ChunkedUploadError) {
                return err.code === 'checksum_mismatch'
                    ? t('deposit.error_checksum_mismatch')
                    : t('deposit.error_assembly')
            }
            if (err instanceof UploadError) {
                if (err.status === 413) {
                    const title = (err.body as { title?: string } | undefined)?.title ?? ''
                    const limit = /(\d+)\s*MB/.exec(title)?.[1]
                    return limit
                        ? t('deposit.error_too_large', { limit })
                        : t('deposit.error_too_large_generic')
                }
                if (err.status === 0 || err.status === 502 || err.status === 503 || err.status === 504) {
                    return t('deposit.error_network')
                }
                return err.message
            }
            return err instanceof Error ? err.message : t('deposit.error_network')
        },
        [t],
    )

    const uploadOne = useCallback(
        async (id: string, { file, originalPath }: DroppedFile) => {
            setFiles(prev =>
                prev.map(f =>
                    f.id === id ? { ...f, status: 'uploading', progress: 0, assembling: false, reason: undefined } : f,
                ),
            )
            try {
                const formData = new FormData()
                formData.append('file', file)
                appendOriginalPath(formData, originalPath)
                await uploadMutation.mutateAsync(formData, {
                    onProgress: ({ loaded, total, phase }: DocumentUploadProgress) => {
                        const progress = total > 0 ? Math.min(100, (loaded / total) * 100) : 0
                        const assembling = phase === 'assembling'
                        setFiles(prev => prev.map(f => (f.id === id ? { ...f, progress, assembling } : f)))
                    },
                })
                setFiles(prev => prev.map(f => (f.id === id ? { ...f, status: 'success', progress: 100 } : f)))
            } catch (err) {
                const reason = failureReason(err)
                setFiles(prev => prev.map(f => (f.id === id ? { ...f, status: 'failed', reason } : f)))
            }
        },
        [uploadMutation, failureReason],
    )

    const deposit = useCallback(
        async (dropped: DroppedFile[]) => {
            if (dropped.length === 0) return

            const queued: DepositFile[] = dropped.map((source, index) => ({
                id: `${source.file.name}-${Date.now()}-${index}`,
                name: source.file.name,
                status: 'uploading',
                progress: 0,
                source,
            }))
            setFiles(prev => [...queued, ...prev])

            // Sequential on purpose: a folder deposit can be hundreds of files
            // and the point here is a reliable, observable trickle rather than
            // a burst that the browser or the API would throttle anyway.
            for (let i = 0; i < dropped.length; i++) {
                await uploadOne(queued[i].id, dropped[i])
            }

            refreshImportViews()
        },
        [uploadOne, refreshImportViews],
    )

    const retry = useCallback(
        async (file: DepositFile) => {
            await uploadOne(file.id, file.source)
            refreshImportViews()
        },
        [uploadOne, refreshImportViews],
    )

    const succeeded = files.filter(f => f.status === 'success').length
    const failed = files.filter(f => f.status === 'failed').length
    const pending = files.filter(f => f.status === 'uploading').length

    return (
        <section className="space-y-3" data-testid="import-deposit-zone">
            <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                    <h2 className="text-sm font-medium text-muted-foreground">{t('deposit.title')}</h2>
                    <p className="text-xs text-muted-foreground">{t('deposit.description')}</p>
                </div>
                {/* Locked: shows the deposit this zone feeds, never retargets it. */}
                <ImportSelect
                    org={org}
                    value={importUid}
                    onChange={() => undefined}
                    locked
                    className="w-full sm:w-72"
                    testidPrefix="import-deposit-select"
                />
            </div>

            <div
                className={`cursor-pointer rounded-lg border-2 border-dashed p-8 text-center transition-colors ${
                    dragOver ? 'border-primary bg-primary/10' : 'border-border hover:border-primary/50'
                }`}
                onClick={() => fileInputRef.current?.click()}
                onDragOver={e => {
                    e.preventDefault()
                    setDragOver(true)
                }}
                onDragLeave={() => setDragOver(false)}
                onDrop={async e => {
                    e.preventDefault()
                    setDragOver(false)
                    await deposit(await collectDroppedFiles(e.dataTransfer))
                }}
                data-testid="import-deposit-dropzone"
            >
                <AppIcon tone="muted" path={mdiUpload} size="xl" className="mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">{t('deposit.drop')}</p>
            </div>

            <input
                ref={fileInputRef}
                type="file"
                multiple
                className="hidden"
                onChange={async e => {
                    await deposit(filesFromFileList(e.target.files))
                    if (fileInputRef.current) fileInputRef.current.value = ''
                }}
                data-testid="import-deposit-input"
            />

            {files.length > 0 && (
                <div className="space-y-1 text-sm" data-testid="import-deposit-results">
                    {files
                        .filter(f => f.status !== 'success')
                        .map(f => (
                            <div
                                key={f.id}
                                className="space-y-1 rounded-md border border-border p-2"
                                data-testid="import-deposit-row"
                                data-status={f.status}
                            >
                                <div className="flex items-center justify-between gap-2">
                                    <span className="truncate">{f.name}</span>
                                    {f.status === 'uploading' && f.assembling && (
                                        <span
                                            className="shrink-0 text-muted-foreground"
                                            data-testid="import-deposit-row-assembling"
                                        >
                                            {t('deposit.assembling')}
                                        </span>
                                    )}
                                    {f.status === 'uploading' && !f.assembling && (
                                        <span
                                            className="shrink-0 tabular-nums text-muted-foreground"
                                            data-testid="import-deposit-row-percent"
                                        >
                                            {Math.round(f.progress)}%
                                        </span>
                                    )}
                                </div>
                                {f.status === 'uploading' && (
                                    <Progress value={f.progress} data-testid="import-deposit-row-progress" />
                                )}
                                {f.status === 'failed' && (
                                    <div className="flex items-center justify-between gap-2 text-destructive">
                                        <span data-testid="import-deposit-row-reason">{f.reason}</span>
                                        <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            onClick={() => retry(f)}
                                            data-testid="import-deposit-row-retry"
                                        >
                                            {t('deposit.retry')}
                                        </Button>
                                    </div>
                                )}
                            </div>
                        ))}
                    {pending > 0 && (
                        <p className="text-muted-foreground" data-testid="import-deposit-uploading">
                            {t('deposit.uploading')}
                        </p>
                    )}
                    {succeeded > 0 && (
                        <p className="flex items-center gap-1 text-success" data-testid="import-deposit-success">
                            <AppIcon path={mdiCheckCircleOutline} size="sm" />
                            {t('deposit.done', { count: succeeded })}
                        </p>
                    )}
                    {failed > 0 && (
                        <p className="flex items-center gap-1 text-destructive" data-testid="import-deposit-failed">
                            <AppIcon path={mdiAlertCircleOutline} size="sm" />
                            {t('deposit.failed', { count: failed })}
                        </p>
                    )}
                </div>
            )}
        </section>
    )
}
